import { Phone, Mail, MapPin, Facebook, Instagram, Clock, ExternalLink, Navigation } from "lucide-react";
import { Link, useLocation } from "wouter";
import { trackBookingClick } from "@/lib/gtm";
import { BUSINESS_DATA } from "@shared/businessData";
import { TREATMENT_BOOKING_URLS } from "@shared/bookingLinks";

const BOOKING = BUSINESS_DATA.bookingUrl;
const PRIMARY_CARE = "https://www.lakewoodprimarycarewellness.com";
const GOOGLE_MAPS = BUSINESS_DATA.mapUrl;
const APPLE_MAPS = "https://maps.apple.com/?address=8210+Walnut+Hill+Ln,+%23516,+Dallas,+TX+75231&ll=32.8818477,-96.7611209&q=Lakewood+Medspa&t=m";
const INSTAGRAM = "https://www.instagram.com/lakewoodmedspadallas";

// Plain crawlable list of every treatment page. The nav menu only renders its links after a
// click or hover, so without this the initial HTML linked to just nine treatment URLs and
// most service pages had four or fewer inbound links. Names match each page's own title.
const ALL_TREATMENT_LINKS: { heading: string; links: { label: string; href: string }[] }[] = [
  {
    heading: "Face & Skin",
    links: [
      { label: "XERF Skin Tightening", href: "/xerf-skin-tightening" },
      { label: "Non-Surgical Face Lift", href: "/non-surgical-face-lift" },
      { label: "Facial Balancing", href: "/facial-balancing" },
      { label: "XERF vs Morpheus8, Thermage & Ultherapy", href: "/xerf-vs-morpheus8-thermage-ultherapy" },
      { label: "Jawline and Chin Filler", href: "/jawline-filler-dallas" },
      { label: "Tear Trough and Under-Eye Filler", href: "/tear-trough-filler-dallas" },
      { label: "Nasolabial Folds and Smile Lines", href: "/nasolabial-folds-filler-dallas" },
      { label: "Hand Filler and Rejuvenation", href: "/hand-filler-dallas" },
      { label: "Turkey Neck and Neck Bands", href: "/turkey-neck-treatment-dallas" },
      { label: "RF Microneedling", href: "/rf-microneedling" },
      { label: "HIFU Skin Tightening", href: "/hifu" },
      { label: "Skin Laxity", href: "/skin-laxity" },
      { label: "Fractional CO₂ Laser", href: "/fractional-co2" },
      { label: "DuoVive Laser", href: "/duovive" },
      { label: "PicoLaser & Tattoo Removal", href: "/pico-laser" },
      { label: "IPL Photofacial", href: "/dpl-photofacial" },
      { label: "AquaFacial", href: "/aquafacial" },
      { label: "Chemical Peels", href: "/chemical-peels" },
      { label: "Acne Scars", href: "/acne-scars" },
      { label: "Pigmentation & Melasma", href: "/pigmentation" },
      { label: "Fine Lines & Wrinkles", href: "/fine-lines-wrinkles" },
      { label: "Dark Circles", href: "/dark-circles" },
    ],
  },
  {
    heading: "Injectables",
    links: [
      { label: "Botox, Dysport & Xeomin", href: "/botox" },
      { label: "Dermal Fillers", href: "/dermal-fillers" },
      { label: "Lip Fillers", href: "/lip-fillers" },
      { label: "Facial Volume", href: "/facial-volume" },
      { label: "Biostimulators", href: "/biostimulators" },
      { label: "PDO Threads", href: "/pdo-threads" },
      { label: "Kybella", href: "/kybella" },
    ],
  },
  {
    heading: "Body & Hair",
    links: [
      { label: "Body Contouring", href: "/body-contouring" },
      { label: "RenaSculpt HIFEM", href: "/renasculpt" },
      { label: "Cryolipolysis", href: "/cryolipolysis" },
      { label: "Hair Restoration (PRP)", href: "/hair-growth" },
      { label: "LLLT Hair Therapy", href: "/lllt-hair-restoration" },
      { label: "Laser Hair Removal", href: "/areslite-hair-removal" },
    ],
  },
  {
    heading: "Wellness",
    links: [
      { label: "Medical Weight Loss", href: "/weight-loss" },
      { label: "Hormone Optimization", href: "/hormone-optimization" },
      { label: "Peptide Therapy", href: "/peptide-therapy" },
      { label: "Functional Lab Testing", href: "/functional-lab-testing" },
      { label: "O-Shot & P-Shot", href: "/prp-sexual-wellness" },
      { label: "Intimate Wellness", href: "/intimate-wellness" },
      { label: "Pain & Regenerative", href: "/pain-regenerative-medicine" },
    ],
  },
];

export default function Footer() {
  const [location] = useLocation();
  const preserveProtectedFooter = location === "/xerf-skin-tightening" || location === "/membership";
  // Paid XERF visitors: footer Book link opens the free XERF consult, not the generic hub.
  const bookingHref = location.split("?")[0] === "/xerf-skin-tightening" ? TREATMENT_BOOKING_URLS.xerf : BOOKING;
  const linkStyle = {
    fontFamily: "'Lato', sans-serif",
    fontSize: "0.8rem",
    color: "#ffffff",
    transition: "color 0.2s",
    cursor: "pointer",
  };
  const hoverIn = (e: React.MouseEvent) => ((e.currentTarget as HTMLElement).style.color = "#ffffff");
  const hoverOut = (e: React.MouseEvent) => ((e.currentTarget as HTMLElement).style.color = "#ffffff");
  const contactHoverIn = (e: React.MouseEvent) => ((e.currentTarget as HTMLElement).style.color = "#ffffff");
  const contactHoverOut = (e: React.MouseEvent) => ((e.currentTarget as HTMLElement).style.color = "#ffffff");

  return (
    <footer style={{ backgroundColor: "#8B6B6B", color: "#FFFFFF" }}>
      <div className="container" style={{ paddingTop: "3.5rem", paddingBottom: "3rem" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: "2.5rem",
        }}>

          {/* Brand Column */}
          <div>
            <div style={{ marginBottom: "1rem" }}>
              <span style={{
                display: "block",
                fontFamily: "'Lato', sans-serif",
                fontSize: "0.6rem",
                fontWeight: 900,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "#ffffff",
                marginBottom: "0.2rem",
              }}>
                LAKEWOOD
              </span>
              <span style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "1.5rem",
                fontStyle: "italic",
                fontWeight: 600,
                color: "#ffffff",
              }}>
                Med Spa
              </span>
            </div>
            <p style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: "0.82rem",
              lineHeight: 1.75,
              color: "#ffffff",
              marginBottom: "1.25rem",
            }}>
              Physician-directed aesthetic treatments on the Texas Health Presbyterian Hospital campus, Dallas, TX.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
              {[
                { icon: Facebook, href: "https://www.facebook.com/lakewoodmedspadallas", label: "Facebook" },
                { icon: Instagram, href: INSTAGRAM, label: "Instagram" },
              ].map(({ icon: Icon, href, label }, i) => (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.2s",
                }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.backgroundColor = "#456080")}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.15)")}
                >
                  <Icon size={14} color="white" />
                </a>
              ))}
            </div>
            <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontFamily: "'Lato', sans-serif", fontSize: "0.75rem", fontWeight: 600, color: "#ffffff", textDecoration: "none", transition: "opacity 0.2s", marginBottom: "0.75rem" }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.8")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
            >
              <Instagram size={13} /> Follow @lakewoodmedspadallas
            </a>
            <br />
            <a href={PRIMARY_CARE} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontFamily: "'Lato', sans-serif", fontSize: "0.72rem", color: "#ffffff", textDecoration: "none", transition: "opacity 0.2s" }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.8")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
            >
              <ExternalLink size={11} /> Lakewood Primary Care &amp; Wellness
            </a>
          </div>

          {/* Treatments */}
          <div>
            <h3 style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#ffffff",
              marginBottom: "1.1rem",
            }}>
              Treatments
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.55rem" }}>
              {[
                { label: "Face & Skin Rejuvenation", href: "/face-skin-rejuvenation" },
                { label: "Injectables & Fillers", href: "/injectables" },
                { label: "Body Contouring", href: "/body-contouring" },
                { label: "Hair Restoration", href: "/hair-growth" },
                { label: "Laser Hair Removal", href: "/areslite-hair-removal" },
                { label: "Skin Analysis", href: "/skin-analysis" },
                { label: "Medical Weight Loss", href: "/weight-loss" },
                { label: "Intimate Wellness", href: "/intimate-wellness" },
                { label: "Pain & Regenerative", href: "/pain-regenerative-medicine" },
              ].map((item) => (
                <li key={item.label}>
                  <Link href={item.href}>
                    <span style={linkStyle}
                      onMouseEnter={hoverIn}
                      onMouseLeave={hoverOut}
                    >
                      {item.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#ffffff",
              marginBottom: "1.1rem",
            }}>
              Quick Links
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.55rem" }}>
              {[
                { label: "Home", href: "/" },
                { label: "About Dr. Kohli", href: "/dr-kohli" },
                { label: "Why Choose Us", href: "/why-choose-us" },
                { label: "Areas We Serve", href: "/areas-we-serve" },
                { label: "Med Spa Near Me", href: "/med-spa-near-me", seoDiscovery: true },
                { label: "Membership & Packages", href: "/membership" },
                { label: "Treatment Packages", href: "/packages", seoDiscovery: true },
                { label: "Gallery & Outcomes", href: "/gallery", seoDiscovery: true },
                { label: "Financing", href: "/financing", seoDiscovery: true },
                { label: "Specials & New", href: "/specials" },
                { label: "Skin Conditions", href: "/face-skin-conditions" },
                { label: "Facial Volume Loss", href: "/facial-volume" },
                { label: "Our Technology", href: "/technology-comparison" },
                { label: "Book Online", href: bookingHref, external: true },
                { label: "Leave a Review", href: "/leave-a-review" },
              ].filter(item => !preserveProtectedFooter || !item.seoDiscovery).map((item) => (
                <li key={item.label}>
                  {"external" in item && item.external ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" style={{
                      ...linkStyle,
                      textDecoration: "none",
                    }}
                      onMouseEnter={hoverIn}
                      onMouseLeave={hoverOut}
                      onClick={item.href === bookingHref ? () => trackBookingClick("footer") : undefined}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link href={item.href}>
                      <span style={linkStyle}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                      >
                        {item.label}
                      </span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#ffffff",
              marginBottom: "1.1rem",
            }}>
              Contact
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.9rem" }}>
              {/* Address with Presbyterian Hospital */}
              <li style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
                <MapPin size={15} style={{ color: "#ffffff", marginTop: 2, flexShrink: 0 }} />
                <div>
                  <a href={GOOGLE_MAPS} target="_blank" rel="noopener noreferrer"
                    style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.83rem", color: "#ffffff", lineHeight: 1.65, textDecoration: "none", transition: "opacity 0.2s", display: "block" }}
                    onMouseEnter={contactHoverIn}
                    onMouseLeave={contactHoverOut}
                  >
                    {BUSINESS_DATA.address.streetAddress}<br />
                    <span style={{ fontSize: "0.78rem", color: "#ffffff" }}>On the Texas Health Presbyterian Hospital campus Dallas</span><br />
                    {BUSINESS_DATA.address.locality}, {BUSINESS_DATA.address.region} {BUSINESS_DATA.address.postalCode}
                  </a>
                  {/* Map links */}
                  <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem" }}>
                    <a href={GOOGLE_MAPS} target="_blank" rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontFamily: "'Lato', sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "#ffffff", textDecoration: "none", transition: "opacity 0.2s", border: "1px solid rgba(255,255,255,0.4)", borderRadius: "4px", padding: "0.2rem 0.5rem" }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.8")}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
                    >
                      <Navigation size={10} /> Google Maps
                    </a>
                    <a href={APPLE_MAPS} target="_blank" rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontFamily: "'Lato', sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "#ffffff", textDecoration: "none", transition: "opacity 0.2s", border: "1px solid rgba(255,255,255,0.4)", borderRadius: "4px", padding: "0.2rem 0.5rem" }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.8")}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
                    >
                      <Navigation size={10} /> Apple Maps
                    </a>
                  </div>
                </div>
              </li>
              <li style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                <Phone size={15} style={{ color: "#ffffff", flexShrink: 0 }} />
                <a href={`tel:${BUSINESS_DATA.phone}`} style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.83rem", color: "#ffffff", transition: "opacity 0.2s", textDecoration: "none" }}
                  onMouseEnter={contactHoverIn}
                  onMouseLeave={contactHoverOut}
                >
                  {BUSINESS_DATA.phoneDisplay}
                </a>
              </li>
              <li style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                <Mail size={15} style={{ color: "#ffffff", flexShrink: 0 }} />
                <a href={`mailto:${BUSINESS_DATA.email}`} style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.83rem", color: "#ffffff", transition: "opacity 0.2s", textDecoration: "none" }}
                  onMouseEnter={contactHoverIn}
                  onMouseLeave={contactHoverOut}
                >
                  {BUSINESS_DATA.email}
                </a>
              </li>
              <li style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
                <Clock size={15} style={{ color: "#ffffff", marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.83rem", color: "#ffffff", lineHeight: 1.65 }}>
                  {BUSINESS_DATA.hours[0].display}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* All treatments: crawlable list. Hidden on the protected paid-ad pages, same as the other SEO-discovery links. */}
        {!preserveProtectedFooter && (
          <nav aria-label="All treatments" style={{
            borderTop: "1px solid rgba(255,255,255,0.2)",
            marginTop: "2.5rem",
            paddingTop: "2rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: "1.75rem 2.5rem",
          }}>
            {ALL_TREATMENT_LINKS.map((group) => (
              <div key={group.heading}>
                <h3 style={{
                  fontFamily: "'Lato', sans-serif",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "#ffffff",
                  marginBottom: "0.9rem",
                }}>
                  {group.heading}
                </h3>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                  {group.links.map((item) => (
                    <li key={item.href}>
                      <Link href={item.href}>
                        <span style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                          {item.label}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        )}

        {/* Bottom bar */}
        <div style={{
          borderTop: "1px solid rgba(255,255,255,0.2)",
          marginTop: "2.5rem",
          paddingTop: "1.5rem",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.75rem",
        }}>
          <p style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.75rem", color: "#ffffff" }}>
            © {new Date().getFullYear()} Lakewood Med Spa. All Rights Reserved.
          </p>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <Link href="/privacy-policy">
              <span style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.75rem", color: "#ffffff", transition: "opacity 0.2s", cursor: "pointer" }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.7")}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
              >
                Privacy Policy
              </span>
            </Link>
            <Link href="/medical-disclaimer">
              <span style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.75rem", color: "#ffffff", transition: "opacity 0.2s", cursor: "pointer" }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.7")}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
              >
                Medical Disclaimer
              </span>
            </Link>
            <Link href="/terms-of-service">
              <span style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.75rem", color: "#ffffff", transition: "opacity 0.2s", cursor: "pointer" }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.7")}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
              >
                Terms of Service
              </span>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
